import React, { useState, useEffect, useRef } from 'react';
import { ScanFace } from 'lucide-react';
import SkillsInput from '../components/SkillsInput';
import EmailBlock from '../components/EmailBlock';
import PhoneBlock from '../components/PhoneBlock';
import RichTextEditor from '../../../atoms/RichTextEditor';
import ImportProfileModal from '../components/ImportProfileModal';
import Toast from '../../../atoms/Toast';
import { API_ENDPOINTS, S3_BASE_URL } from '../../../../config';
import { api } from '../../../../utils/api';
import { useUser } from '../../../../context/UserContext';
import '../../../../styles/profile/basic-info-tab.css';
import '../../../../styles/contact-modal.css';

interface BasicInfo {
  uid: string | null;
  first_name: string | null;
  last_name: string | null;
  headline: string | null;
  current_location: string | null;
  username: string | null;
  skills: string[] | null;
  emails: string | null;
  phones: string | null;
  bio: string | null;
}

interface UserData {
  basic_info: BasicInfo | null;
}

interface BasicInfoTabProps {
  userData: UserData | null;
  basicInfo: BasicInfo | null;
}

export default function BasicInfoTab({ userData, basicInfo }: BasicInfoTabProps) {
  const { userProfile } = useUser();
  const [basicInfoId, setBasicInfoId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [headline, setHeadline] = useState('');
  const [currentLocation, setCurrentLocation] = useState('');
  const [username, setUsername] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  
  const [isDirty, setIsDirty] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [profileImportStatus, setProfileImportStatus] = useState<'none' | 'in_progress' | 'failed' | 'success'>('none');
  const [statusHovered, setStatusHovered] = useState(false);
  const [isInitialStatusLoad, setIsInitialStatusLoad] = useState(true);
  const previousStatusRef = useRef<'none' | 'in_progress' | 'failed' | 'success'>('none');

  const getPhotoUrl = () => {
    if (!userProfile?.img_url) return '';
    if (userProfile.img_url.startsWith('http')) return userProfile.img_url;
    return `${S3_BASE_URL}${userProfile.img_url}`;
  };

  const [initialValues, setInitialValues] = useState({
    firstName: '',
    lastName: '',
    headline: '',
    currentLocation: '',
    username: '',
    skills: [] as string[],
    email: '',
    phone: '',
    bio: '',
  });

  useEffect(() => {
    const fetchProfileImportStatus = async () => {
      try {
        const response = await api.get<{ profile_import_status?: string }>(API_ENDPOINTS.me);
        if (response?.profile_import_status) {
          const status = response.profile_import_status as 'none' | 'in_progress' | 'failed' | 'success';
          previousStatusRef.current = status;
          setProfileImportStatus(status);
          setIsInitialStatusLoad(false);
          return status;
        }
      } catch (error) {
      }
      setIsInitialStatusLoad(false);
      return 'none' as const;
    };

    fetchProfileImportStatus();
  }, []);

  useEffect(() => {
    if (profileImportStatus !== 'in_progress') {
      return;
    }

    const MAX_ATTEMPTS = 60;
    let attempts = 0;

    const interval = setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        clearInterval(interval);
        previousStatusRef.current = profileImportStatus;
        setProfileImportStatus('failed');
        return;
      }
      try {
        const response = await api.get<{ profile_import_status?: string }>(API_ENDPOINTS.me);
        if (response?.profile_import_status) {
          const status = response.profile_import_status as 'none' | 'in_progress' | 'failed' | 'success';
          previousStatusRef.current = profileImportStatus;
          setProfileImportStatus(status);
        }
      } catch (error) {
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [profileImportStatus]);

  useEffect(() => {
    if (isInitialStatusLoad || profileImportStatus !== 'success') {
      return;
    }

    if (previousStatusRef.current === 'success') {
      return;
    }

    const fetchUpdatedProfile = async () => {
      try {
        const response = await api.get<UserData>(API_ENDPOINTS.users.getByUid);
        
        if (response?.basic_info) {
          const importedData = response.basic_info;
          const newValues = {
            firstName: importedData.first_name || '',
            lastName: importedData.last_name || '',
            headline: importedData.headline || '',
            currentLocation: importedData.current_location || '',
            username: importedData.username || '',
            skills: importedData.skills || [],
            email: importedData.emails || '',
            phone: importedData.phones || '',
            bio: importedData.bio || '',
          };

          setBasicInfoId(importedData.uid || null);
          setFirstName(newValues.firstName);
          setLastName(newValues.lastName);
          setHeadline(newValues.headline);
          setCurrentLocation(newValues.currentLocation);
          setUsername(newValues.username);
          setSkills(newValues.skills);
          setEmail(newValues.email);
          setPhone(newValues.phone);
          setBio(newValues.bio);
          setInitialValues(newValues);
          setIsDirty(false);

          setToast({ message: 'Profile imported successfully! ✓', type: 'success' });
        }
      } catch (error) {
        setToast({ message: 'Failed to load updated profile data.', type: 'error' });
      }
    };

    fetchUpdatedProfile();
    previousStatusRef.current = 'success';
  }, [profileImportStatus, isInitialStatusLoad]);

  useEffect(() => {
    if (isInitialStatusLoad || profileImportStatus !== 'failed') {
      return;
    }

    if (previousStatusRef.current === 'failed') {
      return;
    }

    setToast({ 
      message: 'Profile import failed. Please try uploading the file again or use a different CV.', 
      type: 'error' 
    });
    previousStatusRef.current = 'failed';
  }, [profileImportStatus, isInitialStatusLoad]);

  useEffect(() => {
    if (basicInfo) {

      const newValues = {
        firstName: basicInfo.first_name || '',
        lastName: basicInfo.last_name || '',
        headline: basicInfo.headline || '',
        currentLocation: basicInfo.current_location || '',
        username: basicInfo.username || '',
        skills: basicInfo.skills || [],
        email: basicInfo.emails || '',
        phone: basicInfo.phones || '',
        bio: basicInfo.bio || '',
      };


      setBasicInfoId(basicInfo.uid);
      setFirstName(newValues.firstName);
      setLastName(newValues.lastName);
      setHeadline(newValues.headline);
      setCurrentLocation(newValues.currentLocation);
      setUsername(newValues.username);
      setSkills(newValues.skills);
      setEmail(newValues.email);
      setPhone(newValues.phone);
      setBio(newValues.bio);
      
      setInitialValues(newValues);
      setIsDirty(false);
    }
  }, [basicInfo]);

  useEffect(() => {
    const hasChanged =
      firstName !== initialValues.firstName ||
      lastName !== initialValues.lastName ||
      headline !== initialValues.headline ||
      currentLocation !== initialValues.currentLocation ||
      username !== initialValues.username ||
      JSON.stringify(skills) !== JSON.stringify(initialValues.skills) ||
      email !== initialValues.email ||
      phone !== initialValues.phone ||
      bio !== initialValues.bio;

    setIsDirty(hasChanged);
  }, [firstName, lastName, headline, currentLocation, username, skills, email, phone, bio, initialValues]);

  const handleImport = () => {
    setImportModalOpen(true);
  };

  const handleImportFile = async (file: File) => {
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.postFormData(API_ENDPOINTS.users.importProfileFromPdf, formData);

      setProfileImportStatus('in_progress');

      if (response && response.basic_info) {
        const importedData = response.basic_info;
        const newValues = {
          firstName: importedData.first_name || '',
          lastName: importedData.last_name || '',
          headline: importedData.headline || '',
          currentLocation: importedData.current_location || '',
          username: importedData.username || '',
          skills: importedData.skills || [],
          email: importedData.emails || '',
          phone: importedData.phones || '',
          bio: importedData.bio || '',
        };

        setBasicInfoId(importedData.uid || null);
        setFirstName(newValues.firstName);
        setLastName(newValues.lastName);
        setHeadline(newValues.headline);
        setCurrentLocation(newValues.currentLocation);
        setUsername(newValues.username);
        setSkills(newValues.skills);
        setEmail(newValues.email);
        setPhone(newValues.phone);
        setBio(newValues.bio);
        setInitialValues(newValues);
        setIsDirty(false);
      }

      setImportModalOpen(false);
      setToast({ message: 'File uploaded successfully! Processing...', type: 'success' });
    } catch (error) {
      setProfileImportStatus('failed');
      setToast({ 
        message: error instanceof Error ? error.message : 'Failed to import profile. Please try again.', 
        type: 'error' 
      });
      throw error;
    } finally {
      setIsImporting(false);
    }
  };

  const getStatusTooltipText = (): string => {
    if (profileImportStatus === 'in_progress') {
      return 'Recognition is still in progress. Please wait...';
    }
    if (profileImportStatus === 'failed') {
      return 'An error occurred during the last recognition. Please upload the file again or try a different CV.';
    }
    if (profileImportStatus === 'success') {
      return 'Profile has been successfully imported.';
    }
    return '';
  };

  const handleUpdate = async () => {

    setIsUpdating(true);

    try {
      const payload: any = {};

      if (basicInfoId) {
        payload.uid = basicInfoId;
      }

      if (firstName.trim()) payload.first_name = firstName.trim();
      if (lastName.trim()) payload.last_name = lastName.trim();
      if (headline.trim()) payload.headline = headline.trim();
      if (currentLocation.trim()) payload.current_location = currentLocation.trim();
      if (username.trim()) payload.username = username.trim();
      if (skills.length > 0) payload.skills = skills;
      if (email.trim()) payload.emails = email.trim();
      if (phone.trim()) payload.phones = phone.trim();
      if (bio.trim()) payload.bio = bio.trim();


      const response = await api.post(API_ENDPOINTS.users.updateBasicInfo, payload);
      

      if (response && response.uid && !basicInfoId) {
        setBasicInfoId(response.uid);
      }

      setInitialValues({
        firstName,
        lastName,
        headline,
        currentLocation,
        username,
        skills,
        email,
        phone,
        bio,
      });
      setIsDirty(false);

      setToast({ message: 'Basic info updated successfully! ✓', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to update basic info. Please try again.', type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="basic-info-tab contact-modal">
      <div className="basic-info-header">
        <h1 className="basic-info-title">Basic Info</h1>
        <div className="basic-info-actions">
          <div className="basic-info-import-wrapper">
            <button 
              className="basic-info-btn basic-info-btn--import" 
              onClick={handleImport} 
              type="button"
              disabled={profileImportStatus === 'in_progress' || profileImportStatus === 'success'}
            >
              <span className="basic-info-btn-icon">⬇</span>
              Import
            </button>
            {(profileImportStatus === 'in_progress' || profileImportStatus === 'failed' || profileImportStatus === 'success') && (
              <>
                <div
                  className={`basic-info-status-indicator basic-info-status-indicator--${
                    profileImportStatus === 'in_progress' ? 'yellow' : 
                    profileImportStatus === 'failed' ? 'red' : 
                    'green'
                  }`}
                  onMouseEnter={() => setStatusHovered(true)}
                  onMouseLeave={() => setStatusHovered(false)}
                />
                {statusHovered && (
                  <span className="basic-info-status-tooltip basic-info-status-tooltip--visible">
                    {getStatusTooltipText()}
                  </span>
                )}
              </>
            )}
          </div>
          <button 
            className="basic-info-btn basic-info-btn--update" 
            onClick={handleUpdate} 
            type="button"
            disabled={!isDirty || isUpdating}
            style={{ 
              opacity: (!isDirty || isUpdating) ? 0.5 : 1,
              cursor: (!isDirty || isUpdating) ? 'not-allowed' : 'pointer'
            }}
          >
            {isUpdating ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>

      <div className="basic-info-name-row">
        <div className="basic-info-avatar">
          {userProfile?.img_url ? (
            <img 
              src={getPhotoUrl()}
              alt="Profile" 
              className="basic-info-avatar-img"
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <div className="basic-info-avatar-placeholder">
              <ScanFace size={36} className="basic-info-avatar-icon" />
            </div>
          )}
        </div>
        <div className="basic-info-name-fields">
          <div className="basic-info-field">
            <label className="basic-info-label">First Name</label>
            <input
              type="text"
              className="cm-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First Name"
            />
          </div>
          <div className="basic-info-field">
            <label className="basic-info-label">Last Name</label>
            <input
              type="text"
              className="cm-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last Name"
            />
          </div>
        </div>
      </div>

      <div className="basic-info-field">
        <label className="basic-info-label">Headline</label>
        <input
          type="text"
          className="cm-input"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder="Headline"
        />
      </div>

      <div className="basic-info-grid">
        <div className="basic-info-field">
          <label className="basic-info-label">Current Location</label>
          <input
            type="text"
            className="cm-input"
            value={currentLocation}
            onChange={(e) => setCurrentLocation(e.target.value)}
            placeholder="Current Location"
          />
        </div>

        <div className="basic-info-field">
          <label className="basic-info-label">Username</label>
          <input
            type="text"
            className="cm-input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
          />
        </div>
      </div>

      <div className="basic-info-section">
        <label className="basic-info-label">Skills</label>
        <SkillsInput skills={skills} onChange={setSkills} />
      </div>

      <div className="basic-info-grid">
        <div className="basic-info-field">
          <label className="basic-info-label">Emails</label>
          <EmailBlock initial={email} onChange={setEmail} />
        </div>

        <div className="basic-info-field">
          <label className="basic-info-label">Phones</label>
          <PhoneBlock initial={phone} onChange={setPhone} />
        </div>
      </div>

      <div className="basic-info-section">
        <label className="basic-info-label">Bio</label>
        <RichTextEditor
          value={bio}
          onChange={setBio}
          placeholder="Enter your bio..."
          height={200}
        />
      </div>

      <ImportProfileModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={handleImportFile}
        isLoading={isImporting}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

